import React, { useMemo, useState, useEffect, useCallback } from "react";

import { connect } from 'react-redux';
import { useHistory, withRouter } from 'react-router-dom';

import {
	Box, Button, Container, FormControl, Grid, InputLabel, MenuItem,
	Paper, Select, TextField, Typography, Checkbox, ListItemText, CircularProgress, FormHelperText,
	Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, IconButton
} from "@material-ui/core";
import { DateRange } from "@material-ui/icons";
import { ThemeProvider } from "@material-ui/core/styles";

import { Loader } from "../loader";
import { DAYS, FREQS, TIMEZONES, INTERVAL_LIMITS, combineTimes, ensureSchedule, findInvalidNumbers, fmtLocalInput, parseNumberList } from "./utils";

import { clearReduxDataOfComponentInJobStore, getJob, saveJob } from "../../store/actions";

import { theme, useStyles } from "./styles";

const LogicAppRecurrenceSingleState = (props) => {
	const classes = useStyles();
	const history = useHistory();

	const { initial, appId, jobStore } = props;
	const { chartUniqueId, chartData } = props.data

	// single state object (DB shape)
	const [form, setForm] = useState(() =>
		initial || {
			jobName: "",
			description: "",
			// logicAppName: "",
			// triggerName: "",
			recurrence: {
				interval: 1,
				frequency: "Hour",
				timeZone: "India Standard Time",
				startTime: "",
				schedule: { weekDays: [], hours: [], minutes: [] },
			},
		}
	);
	const [hoursText, setHoursText] = useState(""); // comma-separated
	const [minutesText, setMinutesText] = useState(""); // comma-separated
	const [validationErrors, setValidationErrors] = useState({});
	const [pickerOpen, setPickerOpen] = useState(false);
	const [tempStartTime, setTempStartTime] = useState("");

	const [hoursErr, setHoursErr] = useState("");
	const [minutesErr, setMinutesErr] = useState("");

	useEffect(() => { if (initial) setForm(initial); }, [initial]);

	useEffect(() => {
		if (props.match.params?.id) {
			console.log("props.match.params", props.match.params);
			props.getJob({
				data: {
					appid: appId,
					dataModelId: chartData.selectedDataModel,
					documentId: props.match.params.id
				},
				dataUniqueId: `${chartUniqueId}_job_data`
			})
		}
	}, [props.match?.params?.id])

	useEffect(() => {
		if (jobStore?.data?.[`${chartUniqueId}_job_data`]) {
			let data = structuredClone(jobStore.data[`${chartUniqueId}_job_data`])
			console.log("jobStore?.data?.[`${chartUniqueId}_job_data`]", data)
			setForm(data)
			if (data.recurrence?.schedule) {
				if (data.recurrence.schedule.hours?.length) {
					setHoursText(data.recurrence.schedule.hours.join(','))
				}
				if (data.recurrence.schedule.minutes?.length) {
					setMinutesText(data.recurrence.schedule.minutes.join(','))
				}
			}
		}
	}, [jobStore?.data?.[`${chartUniqueId}_job_data`]])

	const canUseAdvanced = useMemo(
		() => ["Day", "Week"].includes(form.recurrence.frequency),
		[form.recurrence.frequency]
	);

	const timesPreview = useMemo(
		() => combineTimes(form.recurrence.schedule?.hours, form.recurrence.schedule?.minutes),
		[form.recurrence.schedule]
	);

	const intervalPhrase = useMemo(() => {
		const i = form.recurrence.interval, f = form.recurrence.frequency;
		if (f === "Week") return i === 1 ? "every week" : `every ${i} weeks`;
		if (f === "Day") return i === 1 ? "every day" : `every ${i} days`;
		if (f === "Hour") return i === 1 ? "every hour" : `every ${i} hours`;
		if (f === "Minute") return i === 1 ? "every minute" : `every ${i} minutes`;
		if (f === "Second") return i === 1 ? "every second" : `every ${i} seconds`;
		if (f === "Month") return i === 1 ? "every month" : `every ${i} months`;
		return `every ${i} ${f.toLowerCase()}(s)`;
	}, [form.recurrence.interval, form.recurrence.frequency]);

	useEffect(() => {
		if (hoursText) {
			updateSchedule({ hours: parseNumberList(hoursText, 0, 23) });
		}
	}, [hoursText])

	useEffect(() => {
		if (minutesText) {
			updateSchedule({ minutes: parseNumberList(minutesText, 0, 59) });
		}
	}, [minutesText])

	const previewText = useMemo(() => {
		const f = form.recurrence.frequency;
		if (f === "Week" || f === "Day") {
			const times = timesPreview.length ? timesPreview.join(", ") : "00:00";
			const dayPart = f === "Week" && form.recurrence.schedule?.weekDays?.length
				? ` on ${form.recurrence.schedule.weekDays.join(", ")}`
				: "";
			return `Runs at ${times}${dayPart} ${intervalPhrase}`;
		}
		if (["Hour", "Minute", "Second"].includes(f)) return `Runs ${intervalPhrase}`;
		if (f === "Month") {
			const times = timesPreview.length ? timesPreview.join(", ") : "00:00";
			return `Runs at ${times} ${intervalPhrase}`;
		}
		return "";
	}, [form, timesPreview, intervalPhrase]);

	// min start time: next minute (blocks past dates and past hours if today)
	const minStart = useMemo(() => {
		const d = new Date();
		d.setSeconds(0, 0);
		d.setMinutes(d.getMinutes() + 1);
		return fmtLocalInput(d);
	}, []);

	// Validate startTime and update errors
	const validateStartTime = useCallback(() => {
		if (!form.recurrence.startTime) {
			setValidationErrors(prev => ({ ...prev, startTime: "Start time is required" }));
			return;
		}

		const minStartDate = new Date(minStart);
		const startTimeDate = new Date(form.recurrence.startTime);
		if (startTimeDate < minStartDate) {
			setValidationErrors(prev => ({
				...prev,
				startTime: `Start time cannot be in the past. Earliest allowed is ${minStart}`
			}));
		} else {
			setValidationErrors(prev => ({ ...prev, startTime: "" }));
		}
	}, [form.recurrence.startTime, minStart]);

	// Run validation when startTime or minStart changes
	useEffect(() => {
		validateStartTime();
	}, [validateStartTime]);

	// updaters
	const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

	const updateRecurrence = (patch) => {
		if (patch?.frequency) {
			patch.schedule = {
				weekDays: [],
				hours: [],
				minutes: []
			}
			setHoursText("");
			setMinutesText("");

			// remove hours and minutes errors
			setValidationErrors({
				...validationErrors,
				hours: "",
				minutes: "",
				interval: ""
			})
		}
		setForm((prev) => ({ ...prev, recurrence: { ...prev.recurrence, ...patch } }));
	}

	const updateSchedule = (patch) => setForm((prev) => ({
		...prev,
		recurrence: {
			...prev.recurrence,
			schedule: { ...ensureSchedule(prev.recurrence).schedule, ...patch }
		},
	}));

	const onHoursChange = (val) => {
		setHoursText(val);
		// const invalid = findInvalidNumbers(val, 0, 23);
		// setHoursErr(invalid.length ? `Invalid: ${invalid.join(", ")}. Allowed 0-23.` : "");
		updateSchedule({ hours: parseNumberList(val, 0, 23) });
	};

	const onMinutesChange = (val) => {
		setMinutesText(val);
		// const invalid = findInvalidNumbers(val, 0, 59);
		// setMinutesErr(invalid.length ? `Invalid: ${invalid.join(", ")}. Allowed 0-59.` : "");
		updateSchedule({ minutes: parseNumberList(val, 0, 59) });
	};

	// Validation function for interval based on frequency
	const validateInterval = (interval, frequency) => {
		if (!interval || interval < 1) {
			return "Interval must be at least 1";
		}

		const maxLimit = INTERVAL_LIMITS[frequency];
		if (maxLimit && interval > maxLimit) {
			return `Interval cannot exceed ${maxLimit} for ${frequency} frequency`;
		}

		return null;
	};

	// Handle interval change with real-time validation
	const handleIntervalChange = (value) => {
		// Allow empty string for user to clear the field
		if (value === "") {
			setValidationErrors(prev => ({ ...prev, interval: "Interval must be at least 1" }));
			updateRecurrence({ interval: "" });
			return;
		}

		const numValue = Number(value);
		// Check if it's a valid number
		if (isNaN(numValue)) {
			setValidationErrors(prev => ({ ...prev, interval: "Please enter a valid number" }));
			return;
		}

		// Validate against frequency limits
		const error = validateInterval(numValue, form.recurrence.frequency);
		if (error) {
			setValidationErrors(prev => ({ ...prev, interval: error }));
			return;
		}

		// Clear error and update value
		setValidationErrors(prev => ({ ...prev, interval: "" }));
		updateRecurrence({ interval: numValue });
	};

	// Handle date/time picker
	const handleOpenPicker = () => {
		setTempStartTime(form.recurrence.startTime || "");
		setPickerOpen(true);
	};

	const handleClosePicker = () => {
		setPickerOpen(false);
		setTempStartTime("");
	};

	const handleConfirmPicker = () => {
		updateRecurrence({ startTime: tempStartTime });
		setPickerOpen(false);
		setTempStartTime("");
	};

	const handleValidations = () => {
		let output = {
			isValid: true,
			info: {},
			data: {}
		};

		let data = structuredClone(form)
		data["recurrenceDescription"] = previewText;
		if (data.recurrence.frequency === "Second" || data.recurrence.frequency === "Minute" || data.recurrence.frequency === "Hour" || data.recurrence.frequency === "Month") {
			delete data.recurrence.schedule;
		}
		if (data.recurrence.frequency === "Day" && data.recurrence.schedule?.weekDays) {
			delete data.recurrence.schedule.weekDays;
		}
		output.data = data;

		if (!data.jobName) {
			output.info.jobName = "Job name is required";
			output.isValid = false;
		} else if (data.jobName.length > 100) {
			output.info.jobName = "Job name cannot exceed 100 characters";
			output.isValid = false;
		}
		if (!data.recurrence.interval || data.recurrence.interval === "") {
			output.info.interval = "Interval must be at least 1";
			output.isValid = false;
		} else {
			// Validate interval against frequency limits
			const intervalError = validateInterval(data.recurrence.interval, data.recurrence.frequency);
			if (intervalError) {
				output.info.interval = intervalError;
				output.isValid = false;
			}
		}
		if (!data.recurrence.frequency) {
			output.info.frequency = "Frequency is required";
			output.isValid = false;
		}
		if (!data.recurrence.startTime) {
			output.info.startTime = "Start time is required";
			output.isValid = false;
		} else {
			// Check if start time is in the past
			const startTimeDate = new Date(data.recurrence.startTime);
			const now = new Date();
			if (startTimeDate <= now) {
				output.info.startTime = `Start time cannot be in the past. Earliest allowed is ${minStart}`;
				output.isValid = false;
			}
		}
		if (!data.recurrence.timeZone) {
			output.info.timeZone = "Time zone is required";
			output.isValid = false;
		}
		if (data.description && data.description.length > 500) {
			output.info.description = "Description cannot exceed 500 characters";
			output.isValid = false;
		}

		if (data.recurrence.frequency === "Day" || data.recurrence.frequency === "Week") {
			if (data.recurrence.schedule?.hours?.length || hoursText?.length) {
				const invalid = findInvalidNumbers(hoursText, 0, 23);
				if (invalid.length) {
					output.info.hours = `Invalid: ${invalid.join(", ")}. Allowed 0-23.`
					output.isValid = false
				}
			} else {
				// output.info.hours = "Hours is required";
				// output.isValid = false;

				delete data.recurrence.schedule.hours;
			}
			if (data.recurrence.schedule?.minutes?.length || minutesText?.length) {
				const invalid = findInvalidNumbers(minutesText, 0, 59);
				if (invalid.length) {
					output.info.minutes = `Invalid: ${invalid.join(", ")}. Allowed 0-59.`
					output.isValid = false
				}
			} else {
				// output.info.minutes = "Minutes is required";
				// output.isValid = false;

				delete data.recurrence.schedule.minutes;

			}
			if (data.recurrence.frequency === "Week") {
				if (!data.recurrence.schedule?.weekDays?.length) {
					// output.info.weekDays = "Week days is required";
					// output.isValid = false;

					delete data.recurrence.schedule.weekDays;
				}
			}
		}

		return output;
	}

	const onSubmit = async (e) => {
		e.preventDefault();
		console.log("it is the json to save in database", form);

		let validations = handleValidations();
		if (!validations.isValid) {
			setValidationErrors(validations.info);
			return;
		}

		let payload = {
			data: validations.data,
			appid: appId,
			dataModelId: chartData.selectedDataModel,
			documentId: props.match.params.id || "",
			dataUniqueId: `${chartUniqueId}_save_job`,
		}
		props.saveJob(payload);
	};

	const handleBackBtnClick = () => {
		const lastPage = chartData.listPage;
		if (lastPage) {
			props.history.push({ pathname: `/${props.match.params.appid}/page/${lastPage}` });
		} else {
			history.goBack()
		}
	}

	useEffect(() => {
		return () => {
			props.clearReduxDataOfComponentInJobStore({
				key: "data",
				subKey: [
					`${chartUniqueId}_job_data`,
					`${chartUniqueId}_save_job`,
				]
			})
		}
	}, [])

	return (
		<ThemeProvider theme={theme}>
			<Box className={classes.root}>
				<Container maxWidth="md">
					{
						jobStore?.loading?.[`${chartUniqueId}_job_data`] ? <Loader /> :
							<Grid container spacing={2}>
								{/* Header */}
								<Grid item xs={12}>
									<Paper className={classes.paper} elevation={1}>
										<Typography variant="subtitle1" gutterBottom>Job</Typography>
										<Grid container spacing={2}>
											<Grid item xs={12} md={4}>
												<TextField
													label="Job name"
													fullWidth
													required
													value={form.jobName}
													onChange={(e) => update({ jobName: e.target.value })}
													placeholder="Enter job name"
													error={!!validationErrors.jobName}
													helperText={validationErrors.jobName || `${form.jobName.length}/100 characters`}
													inputProps={{ maxLength: 100 }}
													variant="outlined"
													InputLabelProps={{
														shrink: true,
														style: { backgroundColor: 'white', paddingLeft: 4, paddingRight: 4 }
													}}
												/>
											</Grid>
											<Grid item xs={12} md={8}>
												<TextField
													label="Job description"
													fullWidth
													value={form.description}
													onChange={(e) => update({ description: e.target.value })}
													placeholder="Enter job description"
													error={!!validationErrors.description}
													helperText={validationErrors.description || `${form.description.length}/500 characters`}
													inputProps={{ maxLength: 500 }}
													variant="outlined"
													InputLabelProps={{
														shrink: true,
														style: { backgroundColor: 'white', paddingLeft: 4, paddingRight: 4 }
													}}
												/>
											</Grid>
											{/* <Grid item xs={12} md={4}>
                                                <TextField
                                                    label="Logic App (workflow) name"
                                                    fullWidth
                                                    required
                                                    value={form.logicAppName}
                                                    InputProps={{ readOnly: logicAppNameRO }}
                                                    disabled={logicAppNameRO}
                                                    helperText="Read-only"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField
                                                    label="Trigger name"
                                                    fullWidth
                                                    required
                                                    value={form.triggerName}
                                                    InputProps={{ readOnly: triggerNameRO }}
                                                    disabled={triggerNameRO}
                                                    helperText="Read-only"
                                                />
                                            </Grid> */}
										</Grid>
									</Paper>
								</Grid>

								{/* Recurrence */}
								<Grid item xs={12}>
									<Paper className={classes.paper} elevation={1}>
										<Typography variant="subtitle1" gutterBottom>Recurrence</Typography>
										<form>
											<Grid container spacing={2}>
												<Grid item xs={6} md={3}>
													<TextField
														label="Interval"
														type="number"
														fullWidth
														required
														inputProps={{
															min: 1,
															step: 1,
															max: INTERVAL_LIMITS[form.recurrence.frequency] || undefined
														}}
														value={form.recurrence.interval}
														onChange={(e) => handleIntervalChange(e.target.value)}
														error={!!validationErrors.interval}
														helperText={validationErrors.interval || `Min: 1, Max: ${INTERVAL_LIMITS[form.recurrence.frequency] || 'N/A'}`}
														variant="outlined"
														InputLabelProps={{
															shrink: true,
															style: { backgroundColor: 'white', paddingLeft: 4, paddingRight: 4 }
														}}
													/>
												</Grid>
												<Grid item xs={6} md={3}>
													<FormControl fullWidth error={!!validationErrors.frequency} variant="outlined">
														<InputLabel id="freq-label" shrink style={{ backgroundColor: 'white', paddingLeft: 4, paddingRight: 4 }}>Frequency</InputLabel>
														<Select
															labelId="freq-label"
															value={form.recurrence.frequency}
															onChange={(e) => updateRecurrence({ frequency: e.target.value })}
															MenuProps={{
																anchorOrigin: {
																	vertical: "bottom",
																	horizontal: "left"
																},
																transformOrigin: {
																	vertical: "top",
																	horizontal: "left"
																},
																getContentAnchorEl: null
															}}
														>
															{FREQS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
														</Select>
														<FormHelperText>{validationErrors.frequency || ""}</FormHelperText>
													</FormControl>
												</Grid>

												<Grid item xs={12} md={6}>
													<FormControl fullWidth error={!!validationErrors.timeZone} variant="outlined">
														<InputLabel id="tz-label" shrink style={{ backgroundColor: 'white', paddingLeft: 4, paddingRight: 4 }}>Time Zone</InputLabel>
														<Select
															labelId="tz-label"
															value={form.recurrence.timeZone}
															onChange={(e) => updateRecurrence({ timeZone: e.target.value })}
															MenuProps={{
																anchorOrigin: {
																	vertical: "bottom",
																	horizontal: "left"
																},
																transformOrigin: {
																	vertical: "top",
																	horizontal: "left"
																},
																getContentAnchorEl: null
															}}
														>
															{TIMEZONES.map((tz) => (
																<MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
															))}
														</Select>
														<FormHelperText>{validationErrors.timeZone || ""}</FormHelperText>
													</FormControl>
												</Grid>

												<Grid item xs={12} md={6}>
													<TextField
														label="Start time"
														fullWidth
														required
														value={form.recurrence.startTime}
														onClick={handleOpenPicker}
														onKeyDown={(e) => e.preventDefault()}
														InputProps={{
															readOnly: true,
															endAdornment: (
																<InputAdornment position="end">
																	<IconButton
																		onClick={handleOpenPicker}
																		edge="end"
																		aria-label="select date and time"
																	>
																		<DateRange />
																	</IconButton>
																</InputAdornment>
															)
														}}
														error={!!validationErrors.startTime}
														helperText={validationErrors.startTime || "Click to select date and time"}
														variant="outlined"
														InputLabelProps={{
															shrink: true,
															style: { backgroundColor: 'white', paddingLeft: 4, paddingRight: 4 }
														}}
													/>
												</Grid>

												{form.recurrence.frequency === "Week" && (
													<Grid item xs={12} md={6}>
														<FormControl fullWidth error={!!validationErrors.weekDays} variant="outlined">
															<InputLabel id="days-label" shrink style={{ backgroundColor: 'white', paddingLeft: 4, paddingRight: 4 }}>On These Days</InputLabel>
															<Select
																labelId="days-label"
																multiple
																value={form.recurrence.schedule?.weekDays || []}
																onChange={(e) => updateSchedule({ weekDays: e.target.value })}
																renderValue={(selected) => (selected || []).join(", ")}
																MenuProps={{
																	anchorOrigin: {
																		vertical: "bottom",
																		horizontal: "left"
																	},
																	transformOrigin: {
																		vertical: "top",
																		horizontal: "left"
																	},
																	getContentAnchorEl: null,
																	PaperProps: {
																		style: {
																			maxHeight: 200,
																			width: 'auto',
																			overflow: 'auto'
																		}
																	}
																}}
															>
																{DAYS.map((d) => (
																	<MenuItem
																		key={d}
																		value={d}
																		style={{
																			paddingTop: 4,
																			paddingBottom: 4,
																			minHeight: 'auto'
																		}}
																	>
																		<Checkbox
																			size="small"
																			color="primary"
																			checked={(form.recurrence.schedule?.weekDays || []).indexOf(d) > -1}
																		/>
																		<ListItemText
																			primary={d}
																			style={{ margin: 0 }}
																		/>
																	</MenuItem>
																))}
															</Select>
															<FormHelperText>{validationErrors.weekDays || ""}</FormHelperText>
														</FormControl>
													</Grid>
												)}

												{canUseAdvanced && (
													<>
														<Grid item xs={12} md={6}>
															<TextField
																label="At These Hours"
																fullWidth
																// required
																placeholder="e.g., 10, 12, 14, 18"
																type="text"
																value={hoursText}
																onChange={(e) => onHoursChange(e.target.value)}
																error={!!validationErrors.hours}
																helperText={validationErrors.hours || "Comma separated, 0–23"}
																variant="outlined"
																InputLabelProps={{
																	shrink: true,
																	style: { backgroundColor: 'white', paddingLeft: 4, paddingRight: 4 }
																}}
															/>
														</Grid>
														<Grid item xs={12} md={6}>
															<TextField
																label="At these minutes"
																fullWidth
																// required
																placeholder="e.g., 0,30"
																type="text"
																value={minutesText}
																onChange={(e) => onMinutesChange(e.target.value)}
																error={!!validationErrors.minutes}
																helperText={validationErrors.minutes || "Comma separated, 0–59"}
																variant="outlined"
																InputLabelProps={{
																	shrink: true,
																	style: { backgroundColor: 'white', paddingLeft: 4, paddingRight: 4 }
																}}
															/>
														</Grid>
													</>
												)}

												<Grid item xs={12}>
													<Box className={classes.preview}>
														<Typography variant="caption" display="block"><b>Preview</b></Typography>
														<Typography variant="caption">{previewText || "-"}</Typography>
													</Box>
												</Grid>

												<Grid item xs={12}>
													<Button
														color="primary"
														variant="contained"
														onClick={(e) => onSubmit(e)}
													>
														{
															jobStore?.loading?.[`${chartUniqueId}_save_job`]
																? <CircularProgress color='#fff' size={24} /> : "Save"
														}
													</Button>
													<Button
														color="secondary"
														variant="contained"
														onClick={() => handleBackBtnClick()}
														style={{ marginLeft: 10 }}
													>
														Back
													</Button>
												</Grid>
											</Grid>
										</form>
									</Paper>
								</Grid>
							</Grid>
					}
				</Container>
			</Box>

			{/* Custom Date/Time Picker Dialog */}
			<Dialog open={pickerOpen} onClose={() => { }} maxWidth="xs" fullWidth disableBackdropClick disableEscapeKeyDown>
				<DialogTitle>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="h6">Select Start Time</Typography>
						<Box>
							<Button onClick={handleClosePicker} color="secondary" size="small" style={{ marginRight: 8 }}>
								Cancel
							</Button>
							<Button onClick={handleConfirmPicker} color="primary" variant="contained" size="small">
								OK
							</Button>
						</Box>
					</Box>
				</DialogTitle>
				<DialogContent>
					<TextField
						label="Date and Time"
						type="datetime-local"
						fullWidth
						value={tempStartTime}
						onChange={(e) => setTempStartTime(e.target.value)}
						inputProps={{
							min: minStart,
							style: { cursor: 'pointer' }
						}}
						onKeyDown={(e) => {
							// Allow only arrow keys, tab, and enter for navigation
							if (!['ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
								e.preventDefault();
							}
						}}
						onPaste={(e) => e.preventDefault()}
						margin="normal"
					/>
				</DialogContent>
			</Dialog>
		</ThemeProvider>
	);
}

const mapDispatchToProps = {
	getJob,
	saveJob,
	clearReduxDataOfComponentInJobStore,
}

const mapStateToProps = state => {
	return {
		formData: state.form,
		jobStore: state.jobs
	};
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LogicAppRecurrenceSingleState))